import React from "react";
import MathText from "./MathText";
import { BookOpen } from "lucide-react";

/**
 * Renders an ideal answer with proper heading hierarchy, glow effects,
 * formula boxes, and rich formatting for descriptive mock test solutions.
 * Uses ➤ for bullets. Strips ** markdown bold and renders as styled headings.
 */
export default function IdealAnswerDisplay({ text }) {
  if (!text) return null;

  const lines = text.split("\n");

  const renderLine = (line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={idx} className="h-2" />;

    // H1: # heading
    if (/^#{1}\s+(.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^#\s+/, "").replace(/\*\*/g, "");
      return (
        <h3 key={idx} className="text-lg font-bold mt-4 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-cyan-300 drop-shadow-[0_0_8px_rgba(52,211,153,0.3)]">
          <MathText text={content} />
        </h3>
      );
    }
    // H2: ## heading
    if (/^#{2}\s+(.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^##\s+/, "").replace(/\*\*/g, "");
      return (
        <h4 key={idx} className="text-base font-semibold mt-3 mb-1.5 text-emerald-300 drop-shadow-[0_0_6px_rgba(52,211,153,0.2)]">
          <MathText text={content} />
        </h4>
      );
    }
    // H3: ### heading
    if (/^#{3}\s+(.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^###\s+/, "").replace(/\*\*/g, "");
      return (
        <h5 key={idx} className="text-sm font-semibold mt-2 mb-1 text-teal-300">
          <MathText text={content} />
        </h5>
      );
    }
    // H4: #### heading
    if (/^#{4,}\s+(.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^#{4,}\s+/, "").replace(/\*\*/g, "");
      return (
        <h6 key={idx} className="text-sm font-medium mt-2 mb-1 text-teal-400/80 italic">
          <MathText text={content} />
        </h6>
      );
    }

    // Bold-only lines (e.g., **Heading**) → render as sub-heading
    if (/^\*\*(.+)\*\*$/.test(trimmed) && trimmed.length < 100) {
      const content = trimmed.replace(/^\*\*/, "").replace(/\*\*$/, "");
      return (
        <h4 key={idx} className="text-base font-semibold mt-3 mb-1.5 text-emerald-300 drop-shadow-[0_0_6px_rgba(52,211,153,0.2)]">
          <MathText text={content} />
        </h4>
      );
    }

    // Formula line: $$...$$ or standalone $...$
    if (/^\$\$.+\$\$$/.test(trimmed) || (/^\$[^$]+\$$/.test(trimmed) && trimmed.length < 120)) {
      return (
        <div key={idx} className="my-2 px-4 py-3 rounded-lg bg-slate-950/60 border border-cyan-800/40 shadow-[0_0_12px_rgba(6,182,212,0.08)] text-center">
          <MathText text={trimmed} className="text-cyan-200 text-sm" />
        </div>
      );
    }

    // Bullet points — use ➤
    if (/^[-•*]\s+(.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^[-•*]\s+/, "").replace(/\*\*/g, "");
      return (
        <div key={idx} className="flex items-start gap-2 ml-2 my-0.5">
          <span className="text-emerald-400 mt-0.5 text-sm flex-shrink-0">➤</span>
          <span className="text-sm text-slate-300 leading-relaxed"><MathText text={content} /></span>
        </div>
      );
    }

    // Numbered points
    if (/^\d+[.)]\s+(.+)$/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
      const content = match[2].replace(/\*\*/g, "");
      return (
        <div key={idx} className="flex items-start gap-2 ml-2 my-0.5">
          <span className="text-emerald-400 font-semibold text-xs mt-0.5 flex-shrink-0 min-w-[1.2rem]">{match[1]}.</span>
          <span className="text-sm text-slate-300 leading-relaxed"><MathText text={content} /></span>
        </div>
      );
    }

    // Regular paragraph — strip ** and render inline bold properly
    let cleaned = trimmed;
    const parts = [];
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIdx = 0;
    let match;
    while ((match = boldRegex.exec(cleaned)) !== null) {
      if (match.index > lastIdx) {
        parts.push({ type: "text", content: cleaned.slice(lastIdx, match.index) });
      }
      parts.push({ type: "bold", content: match[1] });
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < cleaned.length) {
      parts.push({ type: "text", content: cleaned.slice(lastIdx) });
    }

    if (parts.length > 1 || (parts.length === 1 && parts[0].type === "bold")) {
      return (
        <p key={idx} className="text-sm text-slate-300 leading-relaxed my-0.5">
          {parts.map((p, i) =>
            p.type === "bold" ? (
              <strong key={i} className="text-slate-100 font-semibold"><MathText text={p.content} /></strong>
            ) : (
              <MathText key={i} text={p.content} />
            )
          )}
        </p>
      );
    }

    return (
      <p key={idx} className="text-sm text-slate-300 leading-relaxed my-0.5">
        <MathText text={cleaned} />
      </p>
    );
  };

  return (
    <div className="relative mt-3 rounded-xl overflow-hidden" data-testid="ideal-answer-display">
      {/* Glow border effect */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-500/10 via-transparent to-cyan-500/10 pointer-events-none" />
      <div className="relative bg-slate-900/90 border border-emerald-800/30 rounded-xl p-4 backdrop-blur-sm shadow-[0_0_20px_rgba(16,185,129,0.06)]">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-emerald-900/40">
          <div className="p-1.5 rounded-lg bg-emerald-900/40">
            <BookOpen className="w-4 h-4 text-emerald-400" />
          </div>
          <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-cyan-300 uppercase tracking-wider">
            Ideal Answer
          </span>
        </div>
        <div className="space-y-0.5">
          {lines.map((line, idx) => renderLine(line, idx))}
        </div>
      </div>
    </div>
  );
}
